import React, { useEffect, useState } from "react";
import ResponseLoader from "common-ui-components/components/ResponseLoader/ResponseLoader";
import API from "common-ui-components/components/utils/API";
import { IAM_API } from "common-ui-components/components/utils/IAM_API";
import { PRODUCT_CONFIG,PRODUCT_PRIORITY } from "ProductConfig.js";
import { IS_MSP } from "common-ui-components/components/utils/Config.js";
import { setCustomerIdInCookies } from "common-ui-components/accounts/framework/AuthTokenHandler.js";
import { redirectToSignIn } from "app/root/Root.js";
import { isPublicRoute } from "utils.js";

const VALID_PRODUCTS = Object.keys(PRODUCT_CONFIG);

export const getProductFromPath = (path) => {
  const firstSegment = path.split("/")[1];
  return VALID_PRODUCTS.includes(firstSegment) ? firstSegment : null;
};

export const getUserProducts = async () => {
  const res = await IAM_API.get("/user/products");
  let products = res?.data?.body || {};
  return Object.fromEntries(
    Object.entries(PRODUCT_CONFIG).map(([key, config]) => [
      key,
      Object.prototype.hasOwnProperty.call(products, config.id),
    ]),
  );
};

const Main = () => {
  const STATUS = {
    PENDING: "PENDING",
  };

  const [App, setApp] = useState(null);
  const [modulesLoaded, setModulesLoaded] = useState(false);

  const [userDetails, setUserDetails] = useState({
    statusCode: STATUS.PENDING,
    verified: null,
    message: null,
  });

  const [appModes, setAppModes] = useState(null);

  const currentPath = window.location.pathname;
  const productFromUrl = getProductFromPath(currentPath);

  useEffect(() => {
    const bootstrap = async () => {
      /* ---------- PUBLIC ROUTE BYPASS ---------- */
      if (isPublicRoute(currentPath))
      {
        if (productFromUrl)
          setAppModes([productFromUrl]);

        const { default: AppModule } = await import("./App.js");
        setApp(() => AppModule);
        setModulesLoaded(true);
        return;
      }

      /* ---------- NO PRODUCT IN URL ---------- */
      if (!productFromUrl)
      {
        try
        {
          const userProducts = await getUserProducts();
          const firstAvailableProduct = PRODUCT_PRIORITY.find(
            (product) => userProducts[product],
          );

          if (firstAvailableProduct && currentPath !== PRODUCT_CONFIG[firstAvailableProduct].route)
          {
            window.location.replace(PRODUCT_CONFIG[firstAvailableProduct].route);
            return;
          }
        }
        catch (err)
        {
          console.error("Failed to resolve default product", err);
          // if user is unauthorized, need not make any other api calls, user is re-directed to sign-in page by IAM API interceptor.
          if (err?.response?.status === 401) {
            return;
          }
        }
      }

      /* ---------- SET PRODUCT MODE ---------- */
      if (productFromUrl)
      {
        setAppModes([productFromUrl]);
      }

      /* ---------- FETCH USER DETAILS ---------- */
      try
      {
        const response = await API.get("UM/getUserDetails");

        if (response?.status?.success)
        {
          const value = response.body;
          value.isAuthenticated = true;

          //------------------------applab redirection handling---------------------
          if (value.redirectNeeded && value.redirectUrl && value.redirectUrl !== window.location.origin)
          {
            window.location.href = value.redirectUrl;
            return;
          }
          //------------------------xxxxxxxxxxxxxxxxxxxxxxxxxxxx-----------------------

          //-----------------------msp customer list handling----------------------
          let customers = value?.customers || [];

          //adding the all customer option
          let isAllCustomerNeeded = IS_MSP;
          if(isAllCustomerNeeded)
            customers=[...customers, { "1": "All Customer" }]

          //setting a customer id by default and also taking from local storage for taking last used
          let defaultCustomerId = 1;
          if(IS_MSP)
            defaultCustomerId = Object.keys(customers[0])[0];
          const storedCustomerId = localStorage.getItem("customerId");
          const customerExists = customers.some((customer) =>
            Object.keys(customer).includes(String(storedCustomerId))
          );
          const expired = value?.expired ?? true;
          if (!expired && storedCustomerId && customerExists)
            defaultCustomerId = storedCustomerId;
          if(expired)
            defaultCustomerId = 1;

          //setting the customer id in all needed entities
          localStorage.setItem("customerId",defaultCustomerId) //local storage
          setCustomerIdInCookies(defaultCustomerId); //cookie setting
          setUserDetails({ //usedetail setting
            ...value,
            customers,
            isAllCustomerNeeded,
            statusCode: response?.status?.code || 200,
            currentCustomerId: defaultCustomerId
          });
          //------------------------xxxxxxxxxxxxxxxxxxxxxxxxxxxx-----------------------

        }
        else
        {
          setUserDetails({
            statusCode: response?.status?.code || 500,
          });
        }
      }
      catch
      {
        setUserDetails({ statusCode: 500 });
        redirectToSignIn();
      }
      finally
      {
        const { default: AppModule } = await import("./App.js");
        setApp(() => AppModule);
        setModulesLoaded(true);
      }
    };
    bootstrap();
  }, []);

  if (!modulesLoaded || !App)
  {
    return <ResponseLoader fullScreen text="Loading" />;
  }

  return (
    <App
      userDetails={userDetails}
      setUserDetails={setUserDetails}
      appModes={appModes}
    />
  );
};

export default Main;
